import React, { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import SelectSymbol from '../../../components/SelectSymbol/SelectSymbol';
import SwitchInput from '../../../components/SwitchInput/SwitchInput';
import { saveGrid } from '../../../services/AutomationsService';
import { getSymbol } from '../../../services/SymbolsService';
import SymbolPrice from '../../../components/SymbolPrice/SymbolPrice';
import WalletSummary from '../../../components/WalletSummary/WalletSummary';
import { getMemoryIndex } from '../../../services/BeholderService';
import GridTable from './GridTable';
import '../Automations.css';
import '../../../components/PrivatePage/private-page.css';
import LogButton from '../../../components/Logs/LogButton';
import LogView from '../../../components/Logs/LogView';
import GridButton from './GridButton';
import { Icon } from '@iconify/react';
import MaxOpenPositionsField from '../MaxOpenPositionsField';
import { PageCard, PageField } from '../../../components/PrivatePage';
import '../ScheduledBot/scheduled-bot.css';
import {
    parseQuantityValue,
    serializeQuantityValue,
    splitSymbolPair,
} from '../../OrderTemplates/quantityTemplateUtils';
import '../../OrderTemplates/QuantityTemplate.css';

/**
 * props:
 * - data
 * - onSubmit
 * - inline (boolean)
 */
function GridModal(props) {

    const [error, setError] = useState('');

    const DEFAULT_AUTOMATION = {
        symbol: '',
        conditions: '',
        name: '',
        indexes: '',
        actions: [],
        maxOpenPositions: 0,
    }

    const [automation, setAutomation] = useState(DEFAULT_AUTOMATION);

    const DEFAULT_GRID = {
        lowerLimit: '',
        upperLimit: '',
        levels: '',
    }
    const [grid, setGrid] = useState(DEFAULT_GRID);
    const [quantityUnit, setQuantityUnit] = useState('base');
    const [quantityAmount, setQuantityAmount] = useState('');
    const [isMinNotional, setIsMinNotional] = useState(false);

    useEffect(() => {
        if (!props.data) return;
        setAutomation({ ...props.data });

        if (!props.data.id || !props.data.grids || !props.data.grids.length || !props.data.conditions) {
            setGrid({ ...DEFAULT_GRID });
            setQuantityUnit('base');
            setQuantityAmount('');
            setIsMinNotional(false);
            return;
        }

        const conditionSplit = props.data.conditions.split(' && ');
        if (!conditionSplit || conditionSplit.length < 2) return;

        const orderTemplate = props.data.grids[0].orderTemplate;
        if (orderTemplate.quantity === 'MIN_NOTIONAL') {
            setQuantityUnit('base');
            setQuantityAmount('Min. Notional');
            setIsMinNotional(true);
        } else {
            const parsed = parseQuantityValue(orderTemplate.quantity, orderTemplate.quantityMultiplier);
            setQuantityUnit(parsed.unit);
            setQuantityAmount(parsed.amount);
            setIsMinNotional(false);
        }

        setGrid({
            lowerLimit: parseFloat(conditionSplit[0].split('>')[1]),
            upperLimit: parseFloat(conditionSplit[1].split('<')[1]),
            levels: props.data.grids.length + 1,
        });
    }, [props.data])

    const [symbol, setSymbol] = useState(false);
    useEffect(() => {
        if (!automation.symbol) return;

        setError('');
        getSymbol(automation.symbol)
            .then(symbol => setSymbol(symbol))
            .catch(err => {
                console.error(err.response ? err.response.data : err.message);
                setError(err.response ? err.response.data : err.message);
            })
    }, [automation.symbol])

    const { base: baseAsset, quote: quoteAsset } = useMemo(() => {
        if (symbol?.base && symbol?.quote) {
            return { base: symbol.base, quote: symbol.quote };
        }
        return splitSymbolPair(automation.symbol);
    }, [automation.symbol, symbol]);

    function onSymbolChange(event) {
        setAutomation({ ...DEFAULT_AUTOMATION, symbol: event.target.value });
    }

    const [wallet, setWallet] = useState({ base: { symbol: '', qty: 0 }, quote: { symbol: '', qty: 0 } });
    async function loadWallet(symbol) {

        try {
            const baseQty = await getMemoryIndex(symbol.base, 'WALLET', null);
            const quoteQty = await getMemoryIndex(symbol.quote, 'WALLET', null);
            setWallet({ base: { qty: baseQty, symbol: symbol.base }, quote: { qty: quoteQty, symbol: symbol.quote } });
        } catch (err) {
            console.error(err => err.response ? err.response.data : err.message);
            setError(err.message);
        }
    }

    useEffect(() => {
        if (!symbol || !symbol.base) return;
        loadWallet(symbol);
    }, [symbol])

    const [isVisible, setIsVisible] = useState(false);
    useEffect(() => {

        if (props.inline) {
            setIsVisible(true);
            return;
        }

        const modal = document.getElementById('modalGrid');
        if (!modal) return;

        modal.addEventListener('hidden.bs.modal', (event) => {
            setIsVisible(false);
            setGridView(false);
            setAutomation({ ...DEFAULT_AUTOMATION });
            setGrid({ ...DEFAULT_GRID });
            setQuantityUnit('base');
            setQuantityAmount('');
            setIsMinNotional(false);
            setShowLogs(false);
        });

        modal.addEventListener('shown.bs.modal', (event) => {
            setIsVisible(true);
            setGridView(false);
        });

    }, [props.inline]);

    const btnClose = useRef('');
    const btnSave = useRef('');
    const inputTotal = useRef('');

    const validateQuantity = useCallback(() => {
        if (!symbol || !btnSave.current) return;

        if (isMinNotional) {
            if (inputTotal.current) inputTotal.current.value = `${symbol.minNotional}`;
            btnSave.current.disabled = false;
            setError('');
            return;
        }

        const amount = parseFloat(String(quantityAmount).replace(',', '.'));
        if (!Number.isFinite(amount) || amount <= 0) {
            if (inputTotal.current) inputTotal.current.value = '';
            btnSave.current.disabled = true;
            setError('');
            return;
        }

        const lowerLimit = parseFloat(grid.lowerLimit);

        if (quantityUnit === 'base') {
            if (amount < parseFloat(symbol.minLotSize)) {
                setError('Min. Lot Size: ' + symbol.minLotSize);
                btnSave.current.disabled = true;
                return;
            }

            const notional = lowerLimit > 0 ? lowerLimit * amount : 0;
            if (inputTotal.current) {
                inputTotal.current.value = notional ? `${notional}`.substring(0, 10) : '';
            }

            if (lowerLimit > 0 && notional < parseFloat(symbol.minNotional)) {
                setError('Min. Notional: ' + symbol.minNotional);
                btnSave.current.disabled = true;
                return;
            }
        } else {
            if (inputTotal.current) inputTotal.current.value = `${amount}`.substring(0, 10);

            if (amount < parseFloat(symbol.minNotional)) {
                setError('Min. Notional: ' + symbol.minNotional);
                btnSave.current.disabled = true;
                return;
            }

            if (lowerLimit > 0) {
                const estimatedCoinQty = amount / lowerLimit;
                if (estimatedCoinQty < parseFloat(symbol.minLotSize)) {
                    setError('Min. Lot Size at lower limit: ' + symbol.minLotSize);
                    btnSave.current.disabled = true;
                    return;
                }
            }
        }

        btnSave.current.disabled = false;
        setError('');
    }, [grid.lowerLimit, isMinNotional, quantityAmount, quantityUnit, symbol]);

    useEffect(() => {
        validateQuantity();
    }, [validateQuantity]);

    async function onSubmit(event) {
        setError('');

        automation.name = `GRID ${automation.symbol} #${grid.levels}`;
        automation.actions = [{ type: 'GRID' }];
        automation.indexes = `${automation.symbol}:BOOK`;
        automation.conditions = `MEMORY['${automation.symbol}:BOOK'].current.bestAsk>${grid.lowerLimit} && MEMORY['${automation.symbol}:BOOK'].current.bestBid<${grid.upperLimit}`;

        let quantity;
        let quantityMultiplier = 1;

        if (isMinNotional) {
            quantity = 'MIN_NOTIONAL';
        } else {
            const serialized = serializeQuantityValue(quantityUnit, quantityAmount);
            quantity = serialized.quantity;
            quantityMultiplier = serialized.quantityMultiplier;
        }

        saveGrid(automation.id, automation, grid.levels, quantity, quantityMultiplier)
            .then(result => {
                if (props.inline && props.onSubmit) {
                    props.onSubmit(result);
                } else if (btnClose.current) {
                    btnClose.current.click();
                    if (props.onSubmit) props.onSubmit(result);
                }
            })
            .catch(err => {
                console.error(err.response ? err.response.data : err.message);
                setError(err.response ? err.response.data : err.message);
            });
    }

    function onAutomationChange(event) {
        const { id, value, type, checked } = event.target;
        const nextValue = id === 'maxOpenPositions'
            ? Math.max(0, parseInt(value, 10) || 0)
            : (type === 'checkbox' ? checked : value);
        setAutomation((prevState) => ({ ...prevState, [id]: nextValue }));
    }

    function onGridChange(event) {
        const { id, value } = event.target;
        const normalizedValue = value.replace(',', '.');
        setGrid(prevState => ({ ...prevState, [id]: normalizedValue }));
    }

    function onQuantityChange(event) {
        const rawValue = event.target.value;
        if (rawValue === 'Min. Notional') {
            setIsMinNotional(true);
            setQuantityAmount('Min. Notional');
            return;
        }

        setIsMinNotional(false);
        setQuantityAmount(rawValue.replace(',', '.'));
    }

    function onQuantityUnitChange(nextUnit) {
        if (nextUnit === quantityUnit || isMinNotional) return;
        setQuantityUnit(nextUnit);
    }

    const orderValueLabel = quantityUnit === 'quote' && !isMinNotional
        ? 'Order value'
        : 'Est. order value (at lower limit)';

    function renderQuantityField(placeholder) {
        return (
            <div className="qty-template">
                <div className="qty-template__unit-toggle" role="group" aria-label="Grid order size unit">
                    <button
                        type="button"
                        className={`qty-template__unit-btn${quantityUnit === 'base' ? ' is-active' : ' is-inactive'}`}
                        onClick={() => onQuantityUnitChange('base')}
                        aria-pressed={quantityUnit === 'base'}
                        disabled={isMinNotional}
                    >
                        {baseAsset}
                        <small>Coin</small>
                    </button>
                    <button
                        type="button"
                        className={`qty-template__unit-btn${quantityUnit === 'quote' ? ' is-active' : ' is-inactive'}`}
                        onClick={() => onQuantityUnitChange('quote')}
                        aria-pressed={quantityUnit === 'quote'}
                        disabled={isMinNotional}
                    >
                        {quoteAsset}
                        <small>Currency</small>
                    </button>
                </div>
                <input
                    className="form-control"
                    id="quantity"
                    type="text"
                    list="gridQtyList"
                    placeholder={placeholder}
                    value={quantityAmount}
                    onChange={onQuantityChange}
                />
                <datalist id="gridQtyList">
                    <option>Min. Notional</option>
                </datalist>
            </div>
        );
    }

    const [gridView, setGridView] = useState(false)
    function onViewGridsClick(event) {
        if (!gridView) setShowLogs(false);
        setGridView(!gridView);

    }

    const [showLogs, setShowLogs] = useState(false);
    function onLogClick(event) {
        if (!showLogs) setGridView(false);
        setShowLogs(!showLogs);
    }

    if (props.inline) {
        return (
            <div className="bot-setup-form">
                <PageCard
                    icon="mdi:grid"
                    title="Grid setup"
                    description="Configure price limits, levels, and quantity for your grid strategy."
                >
                    <div className="private-page-grid">
                        <PageField label="Trading pair">
                            <SelectSymbol
                                onChange={onSymbolChange}
                                symbol={automation.symbol}
                                onlyFavorites={false}
                                disabled={!!automation.id}
                                fullWidth
                            />
                        </PageField>
                        {isVisible && automation.symbol ? (
                            <PageField label="Current price">
                                <SymbolPrice symbol={automation.symbol} />
                            </PageField>
                        ) : null}
                    </div>

                    <WalletSummary wallet={wallet} />

                    <div className="private-page-grid">
                        <PageField label="Lower limit" htmlFor="lowerLimit">
                            <input
                                className="form-control"
                                id="lowerLimit"
                                type="number"
                                value={grid.lowerLimit || ''}
                                onChange={onGridChange}
                            />
                        </PageField>
                        <PageField label="Upper limit" htmlFor="upperLimit">
                            <input
                                className="form-control"
                                id="upperLimit"
                                type="number"
                                value={grid.upperLimit || ''}
                                onChange={onGridChange}
                            />
                        </PageField>
                        <PageField label="Levels" htmlFor="levels">
                            <input
                                className="form-control"
                                id="levels"
                                type="number"
                                value={grid.levels || ''}
                                disabled={automation.id > 0}
                                onChange={onGridChange}
                            />
                        </PageField>
                        <PageField label="Quantity per level" htmlFor="quantity">
                            {renderQuantityField(symbol?.minLotSize || '0')}
                        </PageField>
                    </div>

                    <PageField label={orderValueLabel} htmlFor="total">
                        <input ref={inputTotal} className="form-control" id="total" type="number" disabled />
                    </PageField>

                    <MaxOpenPositionsField
                        value={automation.maxOpenPositions}
                        onChange={onAutomationChange}
                        hint="0 = unlimited. Limits how many grid levels can hold an open position at once."
                    />

                    <div className="d-flex flex-wrap gap-4 mt-2">
                        <SwitchInput id="isActive" text="Is active?" onChange={onAutomationChange} isChecked={automation.isActive} />
                        <SwitchInput id="logs" text="Enable logs?" onChange={onAutomationChange} isChecked={automation.logs} />
                    </div>
                </PageCard>

                {error ? (
                    <div className="private-page-notice private-page-notice--error" role="alert">
                        <Icon icon="mdi:alert-circle-outline" aria-hidden="true" />
                        <span>{error}</span>
                    </div>
                ) : null}

                <div className="private-page-actions" style={{ justifyContent: 'flex-start' }}>
                    <button
                        ref={btnSave}
                        type="button"
                        className="app-btn app-btn--primary app-btn--md"
                        onClick={onSubmit}
                    >
                        Save grid bot
                    </button>
                </div>
            </div>
        );
    }

    // Default Modal Mode
    return (
        <div className="modal fade" id="modalGrid" tabIndex="-1" role="dialog" aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered" role="document">
                <div className="modal-content">
                    <div className="modal-header">
                        <p className="modal-title" id="modalTitleNotify">{props.data.id ? 'Edit ' : 'New '}Grid</p>
                        <button ref={btnClose} type="button" className="text-gray-900 hover:border-[2px] rounded-md hover:border-gray-400 text-[1.5vw]" data-bs-dismiss="modal" aria-label="close"><Icon icon="iconoir:cancel" className="text-[2vw]" /></button>
                    </div>
                    <div className="modal-body">
                        <div className="form-group">
                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <div className="form-group">
                                        <label htmlFor="symbol">Symbol:</label>
                                        <SelectSymbol onChange={onSymbolChange} symbol={automation.symbol} onlyFavorites={false} disabled={!!automation.id} fullWidth />
                                    </div>
                                </div>
                                <div className="col-md-6 mb-3">
                                    {
                                        isVisible && automation.symbol
                                            ? <SymbolPrice symbol={automation.symbol} />
                                            : <React.Fragment></React.Fragment>
                                    }
                                </div>
                            </div>
                        </div>
                        {
                            !gridView && !showLogs
                                ? (
                                    <React.Fragment>
                                        <div className="form-group">
                                            <WalletSummary wallet={wallet} />
                                            <div className="row">
                                                <div className="col-md-6 mb-3">
                                                    <div className="form-group">
                                                        <label htmlFor="lowerLimit">Lower Limit:</label>
                                                        <input className="form-control" id="lowerLimit" type="number" placeholder="0" value={grid.lowerLimit || ''} onChange={onGridChange} />
                                                    </div>
                                                </div>
                                                <div className="col-md-6 mb-3">
                                                    <div className="form-group">
                                                        <label htmlFor="upperLimit">Upper Limit:</label>
                                                        <input className="form-control" id="upperLimit" type="number" placeholder="0" value={grid.upperLimit || ''} onChange={onGridChange} />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="row">
                                                <div className="col-md-6 mb-3">
                                                    <div className="form-group">
                                                        <label htmlFor="levels">Levels:</label>
                                                        <input className="form-control" id="levels" type="number" placeholder="3" value={grid.levels || ''} disabled={automation.id > 0} onChange={onGridChange} />
                                                    </div>
                                                </div>
                                                <div className="col-md-6 mb-3">
                                                    <div className="form-group">
                                                        <label htmlFor="quantity">Quantity per Level:</label>
                                                        {renderQuantityField(symbol?.minLotSize || '0')}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="row">
                                                <div className="col-md-6 mb-3">
                                                    <div className="form-group">
                                                        <label htmlFor="total">{orderValueLabel}:</label>
                                                        <input ref={inputTotal} className="form-control" id="total" type="number" placeholder="0" disabled />
                                                    </div>
                                                </div>
                                            </div>
                                            <MaxOpenPositionsField
                                                value={automation.maxOpenPositions}
                                                onChange={onAutomationChange}
                                                hint="0 = unlimited. Limits how many grid levels can hold an open position at once."
                                            />
                                            <div className="row">
                                                <div className="col-md-6">
                                                    <div className="form-group">
                                                        <SwitchInput id="isActive" text="Is Active?" onChange={onAutomationChange} isChecked={automation.isActive} />
                                                    </div>
                                                </div>
                                                <div className="col-md-6">
                                                    <div className="form-group">
                                                        <SwitchInput id="logs" text="Enable Logs?" onChange={onAutomationChange} isChecked={automation.logs} />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </React.Fragment>
                                )
                                : <React.Fragment></React.Fragment>
                        }
                        {
                            gridView && !showLogs
                                ? (
                                    <div className="form-group">
                                        <GridTable data={automation.grids} />
                                    </div>
                                )
                                : <React.Fragment></React.Fragment>
                        }
                        {
                            showLogs
                                ? <LogView file={"A:" + automation.id} />
                                : <React.Fragment></React.Fragment>
                        }
                    </div>
                    <div className="modal-footer">
                        {
                            error
                                ? <div className="alert alert-danger mt-1 py-1 col-9">{error}</div>
                                : <React.Fragment></React.Fragment>
                        }
                        <GridButton id={automation.id} onClick={onViewGridsClick} />
                        <LogButton id={automation.id} onClick={onLogClick} />
                        <button ref={btnSave} type="button" className="btn border-[1px] bg-gray-900 text-white btn-sm btn-primary" onClick={onSubmit}>Save</button>
                    </div>
                </div>
            </div>
        </div >
    )
}

export default GridModal;